import React, { useEffect, useState } from "react";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../../components/Breadcrumb";
import axios from "axios";
import { BASE_URL } from "../../helper/api_helper";
import toast from "react-hot-toast";
import { Icon } from "@iconify/react/dist/iconify.js";

const WeQa = () => {
  const [qaEnquiry, setQaInquiry] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 10;

  /* =========================
     API CALL
  ========================= */

  const getWeQaInquiryMasterAdmin = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/get-we-qa-enquiry`);
      if (response.status === 200) {
        setQaInquiry(response.data?.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWeQaInquiryMasterAdmin();
  }, []);

  /* =========================
     SEARCH FILTER
  ========================= */

  const filteredEnquiry = qaEnquiry.filter((item) => {
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();

    return (
      item?.fullname?.toLowerCase().includes(search) ||
      item?.email?.toLowerCase().includes(search) ||
      item?.mobile_number?.toLowerCase().includes(search) ||
      item?.country_code?.toLowerCase().includes(search) ||
      item?.additional_info?.toLowerCase().includes(search)
    );
  });

  /* =========================
     RESET PAGE ON SEARCH
  ========================= */

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  /* =========================
     PAGINATION
  ========================= */

  const paginatedInquiry = filteredEnquiry.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredEnquiry.length / itemsPerPage);

  /* =========================
     DATE FORMAT
  ========================= */

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  return (
    <MasterLayout>
      <Breadcrumb title="WeQa" />

      <div className="col-lg-12">
        <div className="card">
          <div className="card-header py-16 pb-0 px-24 bg-base border border-end-0 border-start-0 border-top-0 d-flex align-items-center flex-wrap justify-content-between">
            <h6 className="text-lg mb-0">WeQa</h6>

            {/* SEARCH */}
            <form className="navbar-search d-flex align-items-center gap-2">
              <input
                type="text"
                className="bg-base h-40-px w-auto form-control"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Icon icon="ion:search-outline" className="icon" />
            </form>
          </div>

          <div className="card-body">
            {/* LOADER */}
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "300px" }}
              >
                <div
                  className="spinner-border"
                  role="status"
                  style={{ color: "#243E85", width: "3rem", height: "3rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : paginatedInquiry.length === 0 ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  minHeight: "200px",
                  fontSize: "1.2rem",
                  color: "#243E85",
                  fontWeight: 500,
                }}
              >
                No inquiries found.
              </div>
            ) : (
              <>
                {/* TABLE */}
                <div className="table-responsive">
                  <table className="table basic-border-table mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Mobile Number</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedInquiry.map((item, index) => (
                        <tr key={index}>
                          <td>{formatDate(item?.createdAt)}</td>
                          <td>{item?.fullname}</td>
                          <td>{item?.email}</td>
                          <td>
                            {item?.mobile_number
                              ? `${item?.country_code} ${item?.mobile_number}`
                              : ""}
                          </td>
                          <td>
                            <button
                              className="btn btn-link p-0"
                              style={{ color: "#243E85" }}
                              onClick={() => {
                                setMessageContent(item?.additional_info || "");
                                setShowMessageModal(true);
                              }}
                            >
                              View Message
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}
                {filteredEnquiry.length > itemsPerPage && (
                  <div className="mt-4 d-flex justify-content-between align-items-center">
                    <span>
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(
                        currentPage * itemsPerPage,
                        filteredEnquiry.length
                      )}{" "}
                      of {filteredEnquiry.length} Inquiry
                    </span>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Prev
                      </button>

                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            prev < totalPages ? prev + 1 : prev
                          )
                        }
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* MESSAGE MODAL */}
      {showMessageModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div className="card" style={{ width: "90%", maxWidth: 490 }}>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Message</h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setShowMessageModal(false)}
              >
                Close
              </button>
            </div>
            <div className="card-body">
              {messageContent ? (
                <p style={{ whiteSpace: "pre-wrap" }}>{messageContent}</p>
              ) : (
                <p className="text-muted">No message provided</p>
              )}
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default WeQa;
